/**
 * Did you mean for brands (RET-256), shared by the search panel (assets/predictive-search.js)
 * and the /search page (snippets/search-did-you-mean.liquid). Loaded only when Theme
 * settings, Launch features, Did you mean for brands is on.
 *
 * The brand list is the JSON in script[data-v2-brands] ([{ n: name, u: url }]). A query is
 * compared with each brand name, and with its first word when the name has more than one:
 * lowercase, accents, spaces and punctuation removed, Damerau-Levenshtein (optimal string
 * alignment) distance up to 1, or up to 2 for names of 7 letters or more. "tra leon" becomes
 * "traleon", one letter from "travelon". A query that is already the brand name gives nothing.
 *
 * window.FlushDidYouMean: brands(), setBrands(list), match(query), wordsFound(query, hay), fetchPicks(url, suffix,
 * signal), fill(template, brand). On the /search page it fills every [data-v2-dym] container.
 */
(() => {
  if (window.FlushDidYouMean) return;

  const normalize = (text) =>
    String(text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]/g, '');

  const words = (text) =>
    String(text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .split(/[^a-z0-9]+/)
      .filter(Boolean);

  /* Optimal string alignment distance, stopping early once every cell in a row passes max. */
  function distance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    const rows = [];
    for (let i = 0; i <= a.length; i += 1) {
      rows[i] = [i];
    }
    for (let j = 1; j <= b.length; j += 1) rows[0][j] = j;
    for (let i = 1; i <= a.length; i += 1) {
      let rowMin = Infinity;
      for (let j = 1; j <= b.length; j += 1) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let value = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          value = Math.min(value, rows[i - 2][j - 2] + 1);
        }
        rows[i][j] = value;
        if (value < rowMin) rowMin = value;
      }
      if (rowMin > max) return max + 1;
    }
    return rows[a.length][b.length];
  }

  let brandCache = null;
  function brands() {
    if (brandCache) return brandCache;
    const el = document.querySelector('script[data-v2-brands]');
    try {
      const list = JSON.parse(el ? el.textContent : '[]');
      brandCache = Array.isArray(list) ? list.filter((b) => b && b.n && b.u) : [];
    } catch (error) {
      brandCache = [];
    }
    return brandCache;
  }

  /* The search panel loads the brand list when it first opens and hands it over here. */
  function setBrands(list) {
    brandCache = Array.isArray(list) ? list.filter((b) => b && b.n && b.u) : [];
  }

  /* The closest brand within the allowed distance, or null. */
  function match(query) {
    const plain = String(query || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const key = normalize(query);
    if (key.length < 4) return null;
    let best = null;
    for (const brand of brands()) {
      const nameWords = words(brand.n);
      const targets = [normalize(brand.n)];
      if (nameWords.length > 1 && nameWords[0].length >= 4) targets.push(nameWords[0]);
      // Already the brand's own name (or its first word): not a typo, nothing to suggest.
      if (plain === brand.n.trim().toLowerCase() || (nameWords.length > 1 && plain === nameWords[0])) return null;
      for (const target of targets) {
        const max = target.length >= 7 ? 2 : 1;
        const d = distance(key, target, max);
        if (d <= max && (!best || d < best.distance)) best = { name: brand.n, url: brand.u, distance: d };
      }
    }
    return best;
  }

  /* True when every query word starts a word in hay: the query is ordinary words the
     results already contain, so a brand suggestion would only get in the way. */
  function wordsFound(query, hay) {
    const hayWords = words(hay);
    const queryWords = words(query);
    if (!queryWords.length || !hayWords.length) return false;
    return queryWords.every((w) => hayWords.some((h) => h.startsWith(w)));
  }

  function picksUrl(url, suffix) {
    return `${url}${url.includes('?') ? '&' : '?'}${suffix}`;
  }

  /* Product cards can repeat a card already on the page: prefix every id in a fetched
     fragment and the references to them, so ids stay unique. */
  function scopeIds(root, prefix) {
    const ids = new Map();
    root.querySelectorAll('[id]').forEach((el) => {
      const next = `${prefix}${el.id}`;
      ids.set(el.id, next);
      el.id = next;
    });
    if (!ids.size) return;
    root.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], label[for]').forEach((el) => {
      ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for'].forEach((attr) => {
        const value = el.getAttribute(attr);
        if (!value) return;
        el.setAttribute(attr, value.split(/\s+/).map((id) => ids.get(id) || id).join(' '));
      });
    });
  }

  async function fetchPicks(url, suffix, signal) {
    const res = await fetch(picksUrl(url, suffix), { signal });
    if (!res.ok) throw new Error(`Brand picks ${res.status}`);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const list = doc.querySelector('[data-brand-picks] ul');
    return list ? document.importNode(list, true) : null;
  }

  function fill(template, brand) {
    return String(template || '').replace('[brand]', () => brand);
  }

  /* /search page: "Did you mean <brand>?", its best sellers and Shop all <brand>. */
  function fillPage(container) {
    // A page that already shows matches passes its titles, vendors and types: when every
    // typed word starts a word in them, there is nothing to suggest (same rule as the panel).
    if (container.dataset.hay && wordsFound(container.dataset.terms, container.dataset.hay)) return;
    const brand = match(container.dataset.terms);
    if (!brand) return;
    const heading = document.createElement('h2');
    heading.className = 'v2-dym__title';
    const [before, after = ''] = fill(container.dataset.heading, '\u0000').split('\u0000');
    const link = document.createElement('a');
    link.href = brand.url;
    link.className = 'v2-dym__brand';
    link.textContent = brand.name;
    heading.append(before, link, after);
    container.append(heading);
    // The live region after the container gets the sentence alone, not the cards.
    const status = container.nextElementSibling;
    if (status && status.matches('[data-v2-dym-status]')) status.textContent = heading.textContent;

    fetchPicks(brand.url, container.dataset.picksUrlSuffix)
      .then((list) => {
        if (!list || !list.children.length) return;
        scopeIds(list, 'Dym');
        const label = document.createElement('h3');
        label.className = 'v2-search__heading';
        label.textContent = fill(container.dataset.picksHeading, brand.name);
        const shopAll = document.createElement('a');
        shopAll.className = 'btn btn--outline v2-dym__shop-all';
        shopAll.href = brand.url;
        shopAll.textContent = fill(container.dataset.shopAll, brand.name);
        const picks = document.createElement('div');
        picks.className = 'v2-dym__picks';
        picks.append(label, list, shopAll);
        container.append(picks);
      })
      .catch(() => {});
  }

  window.FlushDidYouMean = { brands, setBrands, match, wordsFound, fetchPicks, fill, distance };

  const init = () => document.querySelectorAll('[data-v2-dym]').forEach(fillPage);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
